import { useMemo } from 'react';
import { sanitizeExplanationHtml } from '../lib/explanationHtml';

interface RichExplanationProps {
  /** Cached explanation HTML from /ai/explain or /ai/tutor. Untrusted. */
  html: string;
  className?: string;
}

// Renders a cached HTML explanation (BP 11).
//
// This is the ONLY component in the app that renders model-generated HTML, and
// the only place `dangerouslySetInnerHTML` is used for it. It sanitises on every
// render — the sanitised string is memoised on the input, so the cost is paid
// once per explanation, but there is no path through this component that reaches
// the DOM without it. If you find yourself wanting the raw html elsewhere, mount
// this instead.
//
// Everything visual lives in the `.xh` block in styles/app.css, bound to --ws-*
// tokens so it themes correctly in all four themes (PlanXi light, explicit
// light, dark, paper). The generated markup is forbidden from carrying inline
// colours for exactly that reason.
export default function RichExplanation({ html, className = '' }: RichExplanationProps) {
  const safe = useMemo(() => sanitizeExplanationHtml(html), [html]);
  if (!safe) return null;
  return (
    <div
      className={`xh-root ${className}`}
      // Sanitised immediately above by DOMPurify against a strict tag/attribute
      // allowlist (no script, no on* handlers, no href/src, no style).
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
